import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WhbabtecLanguages, WhUserDto, WHBABTEC_ENDPOINTS } from '@whbabtec-types';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { LanguageService } from './language.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private user$$ = new BehaviorSubject<WhUserDto | undefined>(undefined);

  /** Prevents multiple concurrent or duplicate initializations */
  private initPromise: Promise<void> | null = null;

  constructor(
    private authService: AuthService,
    private languageService: LanguageService,
    private httpClient: HttpClient
  ) {}

  get user$(): Observable<WhUserDto | undefined> {
    return this.user$$.asObservable();
  }

  public init(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.fetchUser();
    }
    return this.initPromise;
  }

  public async refresh(): Promise<void> {
    this.initPromise = null;
    await this.init();
  }

  public isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  public logout(): void {
    this.authService.logout();
  }

  public getLanguage(): WhbabtecLanguages {
    return this.languageService.getCurrentLanguage();
  }

  /**
   * Returns the access token for backward compatibility with components
   * that manually set Authorization headers (complaints-form, complaints-table, etc.).
   * Returns an object matching the old { token: string } shape.
   */
  public getAccessToken(): { token: string } | undefined {
    const token = this.authService.getToken();
    if (!token) {
      return undefined;
    }
    return { token };
  }

  private async fetchUser(): Promise<void> {
    if (!this.authService.isLoggedIn()) {
      this.user$$.next(undefined);
      this.initPromise = null;
      return;
    }

    const token = this.authService.getToken();
    const user = await firstValueFrom(
      this.httpClient.get<WhUserDto>(WHBABTEC_ENDPOINTS.USER, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    this.user$$.next(user);
  }
}
